
import React, { useMemo } from 'react';
import { List } from 'react-window';
import { Order, Driver, SystemConfig } from '../types';
import { OrderCard } from './OrderCard';
import { Bike, ShoppingBag, Route, Layers } from 'lucide-react';
import { getFleetLabel } from '../services/logistics/core/routePresentation';

// Item types for the virtualized list
type ProcessedOrder = Order & { 
  groupId?: string | null; 
  groupIndex?: number; 
  totalInGroup?: number; 
  groupColor?: string;
  routeTag?: string;
  routeMode?: 'own' | 'third';
  routeAssignee?: string;
};

type ListItem = 
  | { type: 'SECTION_HEADER'; title: string; count: number; icon: any; colorClass: string }
  | { type: 'GROUP_HEADER'; order: ProcessedOrder }
  | { type: 'ORDER'; order: ProcessedOrder; isFirstInGroup?: boolean; isInGroup?: boolean }
  | { type: 'GROUP_END' };

interface OrderListProps {
  deliveryOrders: ProcessedOrder[];
  takeoutOrders: Order[];
  focusedOrderId: string | null;
  onToggleFocus: (id: string | null) => void;
  onDeleteOrder: (id: string) => void;
  onEditOrder: (order: Order) => void;
  drivers: Driver[];
  onAssignDriver: (orderId: string, driverId: string) => void;
  config: SystemConfig;
  isSimpleView: boolean;
  onDispatchCluster: (orders: Order[], mode: 'own' | 'third', driverId?: string) => void;
  onToggleReady: (orderId: string) => void;
  onNotifyDelay: (orderId: string) => void;
  onConfirmOrder: (orderId: string) => void;
  onTrackOrder: (orderId: string) => void;
  onManualComplete: (orderId: string) => void;
  cardViewMode: 'standard' | 'compact';
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  suggestion?: any;
  timeTick: number;
  height: number;
}

const SectionHeader = ({ icon: Icon, title, count, colorClass }: any) => (
  <div className="bg-app-bg/95 backdrop-blur-md px-3 py-2 border-y border-border-dim flex items-center justify-between mb-1 shadow-sm h-[36px]">
    <div className="flex items-center gap-2">
      <div className={`p-1 rounded-lg bg-card-bg border border-border-dim ${colorClass}`}><Icon className="w-3 h-3" /></div>
      <span className="text-xs font-black uppercase-ops text-text-secondary">{title}</span>
    </div>
    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full bg-card-bg border border-border-dim ${colorClass}`}>{count}</span>
  </div>
);

const GroupHeader = ({ order }: any) => (
  <div
    className="flex items-center gap-2 px-2 py-1 mt-1 mb-1 rounded-lg mx-2 h-[28px]"
    style={{ 
      backgroundColor: `${order.groupColor}10`,
      borderLeft: `3px solid ${order.groupColor}`
    }}
  >
    <Route className="w-3 h-3" style={{ color: order.groupColor }} />
      <span className="text-[10px] font-black uppercase-ops" style={{ color: order.groupColor }}>
      {order.routeTag} • {getFleetLabel(order.routeMode || 'own')} • {order.routeAssignee || 'Sem responsavel'} • {order.totalInGroup} pedidos
      </span>
    <Layers className="w-2.5 h-2.5 ml-auto" style={{ color: order.groupColor }} />
  </div>
);

export const OrderList: React.FC<OrderListProps> = (props) => {
  const { 
    deliveryOrders, takeoutOrders, cardViewMode, height,
    focusedOrderId, onToggleFocus, onDeleteOrder, onEditOrder,
    drivers, onAssignDriver, config, isSimpleView, onDispatchCluster,
    onToggleReady, onNotifyDelay, onConfirmOrder, onTrackOrder,
    onManualComplete, selectedIds, onToggleSelection, suggestion, timeTick
  } = props;

  // Flatten orders into a list of items
  const items = useMemo(() => {
    const result: ListItem[] = [];

    if (deliveryOrders.length > 0) {
      result.push({ 
        type: 'SECTION_HEADER', 
        title: 'Entregas (Logística)', 
        count: deliveryOrders.length, 
        icon: Bike, 
        colorClass: 'text-sal-cyber-teal' 
      });

      deliveryOrders.forEach((order, index) => {
        const isGroupStart = order.groupId && (index === 0 || deliveryOrders[index - 1]?.groupId !== order.groupId);
        const isGroupEnd = order.groupId && (index === deliveryOrders.length - 1 || deliveryOrders[index + 1]?.groupId !== order.groupId);
        const isInGroup = order.groupId && order.totalInGroup > 1;

        if (isGroupStart) {
          result.push({ type: 'GROUP_HEADER', order });
        }

        result.push({ type: 'ORDER', order, isFirstInGroup: isGroupStart, isInGroup });

        if (isGroupEnd && isInGroup) {
          result.push({ type: 'GROUP_END' });
        }
      });
    }

    if (takeoutOrders.length > 0) {
      result.push({ 
        type: 'SECTION_HEADER', 
        title: 'Retiradas (Balcão)', 
        count: takeoutOrders.length, 
        icon: ShoppingBag, 
        colorClass: 'text-amber-500' 
      });

      takeoutOrders.forEach(order => {
        result.push({ type: 'ORDER', order, isInGroup: false });
      });
    }

    return result;
  }, [deliveryOrders, takeoutOrders]);

  const getItemSize = (index: number) => {
    const item = items[index];
    if (!item) return 0;

    switch (item.type) {
      case 'SECTION_HEADER': return 40;
      case 'GROUP_HEADER': return 32;
      case 'GROUP_END': return 12;
      case 'ORDER': 
        return cardViewMode === 'compact' ? 72 : 116;
      default: return 0;
    }
  };

  const Row = ({ index, style, ...rowProps }: any) => {
    const data = rowProps as any;
    const { 
      items, focusedOrderId, selectedIds, onToggleSelection, onToggleFocus, 
      onDeleteOrder, onEditOrder, drivers, onAssignDriver, config, 
      cardViewMode, onDispatchCluster, onToggleReady, onNotifyDelay, 
      onConfirmOrder, onTrackOrder, onManualComplete, suggestion, timeTick,
      isSimpleView
    } = data;
    
    const item = items[index];
    if (!item) return null;

    return (
      <div style={style} className="px-2">
        {item.type === 'SECTION_HEADER' && (
          <SectionHeader {...item} />
        )}
        {item.type === 'GROUP_HEADER' && (
          <GroupHeader order={item.order} />
        )}
        {item.type === 'ORDER' && (
          <div 
            className={`relative ${item.isInGroup ? 'pl-1' : ''}`}
            style={item.isInGroup ? { 
              borderLeft: `2px solid ${item.order.groupColor}`,
              marginLeft: '4px'
            } : {}}
          >
            {item.isInGroup && (
              <div 
                className="absolute left-[-6px] top-1/2 -translate-y-1/2 w-4 h-4 rounded-full flex items-center justify-center text-xs font-black text-white z-10 shadow-sm"
                style={{ backgroundColor: item.order.groupColor }}
              >
                {item.order.groupIndex}
              </div>
            )}
            <OrderCard 
              order={item.order} 
              isSelected={focusedOrderId === item.order.id} 
              isFocused={selectedIds.has(item.order.id)} 
              onToggle={onToggleSelection}
              onFocus={onToggleFocus} 
              onDelete={onDeleteOrder} 
              onEdit={onEditOrder} 
              drivers={drivers} 
              onAssignDriver={onAssignDriver} 
              systemConfig={config} 
              isCompact={cardViewMode === 'compact'} 
              onDispatchCluster={onDispatchCluster} 
              onToggleReady={onToggleReady} 
              onNotifyDelay={onNotifyDelay} 
              isSimpleView={isSimpleView} 
              onConfirmOrder={onConfirmOrder} 
              onTrackOrder={onTrackOrder} 
              onManualComplete={onManualComplete} 
              suggestion={suggestion}
              timeTick={timeTick}
            />
          </div>
        )}
        {item.type === 'GROUP_END' && (
          <div className="h-px bg-border-dim/50 my-2" />
        )}
      </div>
    );
  };

  const itemData = useMemo(() => ({
    items,
    focusedOrderId,
    selectedIds,
    onToggleSelection,
    onToggleFocus,
    onDeleteOrder,
    onEditOrder,
    drivers,
    onAssignDriver,
    config,
    cardViewMode,
    onDispatchCluster,
    onToggleReady,
    onNotifyDelay,
    onConfirmOrder,
    onTrackOrder,
    onManualComplete,
    suggestion,
    timeTick,
    isSimpleView
  }), [
    items, focusedOrderId, selectedIds, onToggleSelection, onToggleFocus, 
    onDeleteOrder, onEditOrder, drivers, onAssignDriver, config, 
    cardViewMode, onDispatchCluster, onToggleReady, onNotifyDelay, 
    onConfirmOrder, onTrackOrder, onManualComplete, suggestion, timeTick,
    isSimpleView
  ]);

  return (
    <List
      rowCount={items.length}
      rowHeight={getItemSize}
      rowProps={itemData}
      rowComponent={Row}
      className="no-scrollbar"
      style={{ height, width: '100%' }}
    />
  );
};
